import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { CSSProperties, FC, ReactNode } from "react";
import { INK } from "../../theme/tokens";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";

type Props = {
	mainText: string;
	subText?: string;
	smallText?: string;
	mainTextSx?: SxProps<Theme>;
	subTextSx?: SxProps<Theme>;
	smallTextSx?: SxProps<Theme>;
	verticalGap?: CSSProperties["gap"];
	horizontalGap?: CSSProperties["gap"];
	mainTextTooltip?: ReactNode;
	subTextTooltip?: ReactNode;
	smallTextTooltip?: ReactNode;
};

const defaultMainTextSx = {
	fontWeight: 700,
	fontSize: { xl: "26px", xs: "16px" },
	lineHeight: "30px",
	color: INK.SECONDARY,
	textWrap: "nowrap",
} satisfies SxProps<Theme>;

/** Exported so a caller can keep the subtext styling and override just one property (e.g. font size). */
export const defaultSubTextSx = {
	fontWeight: 700,
	fontSize: { xl: "16px", xs: "14px" },
	lineHeight: "20px",
	color: alpha(INK.SECONDARY, 0.4),
	textWrap: "nowrap",
} satisfies SxProps<Theme>;

const defaultSmallTextSx = {
	fontWeight: 400,
	fontSize: { xxl: "13px", xl: "12px", xs: "10px" },
	lineHeight: "15px",
	color: alpha(INK.SECONDARY, 0.5),
	textWrap: "wrap",
	whiteSpace: "pre-wrap",
} satisfies SxProps<Theme>;

export const DashboardTypography: FC<Props> = ({
	mainText,
	subText,
	mainTextSx = defaultMainTextSx,
	subTextSx = defaultSubTextSx,
	verticalGap = 1,
	horizontalGap = 1,
	smallText,
	smallTextSx = defaultSmallTextSx,
	mainTextTooltip,
	subTextTooltip,
	smallTextTooltip,
}) => {
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "start",
				gap: verticalGap,
				// Cards lay their content out with align-items: start, which sizes this box to its
				// content — so a long nowrap subText grows past the card border instead of being clipped
				// by it. Capping at the card's width lets a caller opt into ellipsis. No effect when the
				// content already fits.
				maxWidth: "100%",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "baseline",
					gap: horizontalGap,
					maxWidth: "100%",
				}}
			>
				{mainTextTooltip ? (
					<InHouseTooltip title={mainTextTooltip}>
						<Typography sx={mainTextSx}>{mainText}</Typography>
					</InHouseTooltip>
				) : (
					<Typography sx={mainTextSx}>{mainText}</Typography>
				)}
				{subText &&
					(subTextTooltip ? (
						<InHouseTooltip title={subTextTooltip}>
							<Typography sx={subTextSx}>{subText}</Typography>
						</InHouseTooltip>
					) : (
						<Typography sx={subTextSx}>{subText}</Typography>
					))}
			</Box>
			{smallText &&
				(smallTextTooltip ? (
					<InHouseTooltip title={smallTextTooltip}>
						<Typography sx={smallTextSx}>{smallText}</Typography>
					</InHouseTooltip>
				) : (
					<Typography sx={smallTextSx}>{smallText}</Typography>
				))}
		</Box>
	);
};
